import { useParams } from "react-router-dom";
import { FC, useMemo, useState } from "react";
import styled from "styled-components";
import { useInfiniteQuery } from "@tanstack/react-query";

import {
  LargeTh,
  SortableTh,
  Table,
  TableContent,
  Th,
} from "@/components/Table";
import Text from "@/components/Text";
import {
  PaginatedResponse,
  SortState,
  TopTopicsResource,
  TopVideosResource,
  VideoTopicResource,
} from "@/types/resources";
import { SortDirection, TopicsLevel } from "@/utils/enums";
import InfoTooltip from "@/components/InfoTooltip";
import useDidUpdate from "@/hooks/useDidUpdate";
import { api } from "@/utils/api";
import { TopTopicsTabs } from "./TopTopicsTable";
import TopTopicsInnerTableRow from "./TopTopicsInnerTableRow";
import InfiniteLoader from "@/components/InfiniteLoader";
import { SrOnly } from "@/components/Layout/Parts";

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DEFAULT,
  property: "views",
};

const StyledTable = styled(Table)`
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    background: #f5f5f5 !important;
    border-bottom: 1px solid ${({ theme }) => theme.colors.white} !important;
    &:nth-child(1) {
      width: 128px !important;
      min-width: 128px !important;
      padding-left: 24px;
      position: static !important;
    }
    &:nth-child(2) {
      min-width: 200px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

interface Props {
  activeTab: number;
  filteredChannelsIds: number[];
  topic?: TopTopicsResource;
  topics?: VideoTopicResource[];
  level?: TopicsLevel;
}

const TopTopicsInnerTable: FC<Props> = ({
  activeTab,
  filteredChannelsIds,
  topic,
  topics,
  level,
}) => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const { teamId } = useParams();

  const params = {
    "filter[was_live]": 0,
    "filter[unreachable]": 0,
    ...(activeTab !== TopTopicsTabs.All && {
      "filter[competitor]": activeTab,
    }),
    ...(filteredChannelsIds?.length && {
      "filter[channel_id]": filteredChannelsIds.join(","),
    }),
    ...(sort.direction !== "DEFAULT" && {
      sort: `${sort.direction === "DESC" ? "-" : ""}${sort.property}`,
    }),
    ...(!!topic && {
      "filter[topic]": topic.id,
      topic_operator: "AND",
      topic_level: level,
    }),
    ...(!!topics?.length && {
      "filter[topic]": topics?.map((topic) => topic.id).join(","),
      topic_operator: "AND",
    }),
  };

  const fetchVideos = ({ pageParam = `/v1/teams/${teamId}/videos/top` }) => {
    return api.get<never, PaginatedResponse<TopVideosResource>>(pageParam, {
      params,
    });
  };

  const videosQueryKey = topic
    ? ["top-topics-videos", { teamId, topics: topic }]
    : [
        "top-topics-videos",
        { teamId, topics: topics?.map((t) => t.id).join(",") },
      ];

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
    remove,
  } = useInfiniteQuery<PaginatedResponse<TopVideosResource>>(
    videosQueryKey,
    fetchVideos,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    }
  );

  useDidUpdate(() => {
    remove();
    refetch();
  }, [sort]);

  const videos = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<TopVideosResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        []
      ),
    [data]
  );

  return (
    <>
      <StyledTable>
        <thead>
          <tr>
            <Th>Thumbnail</Th>
            <Th>
              Video title
              <br />
              <Text as="span" $color="gray" $type="extraSmall">
                Channel name
              </Text>
            </Th>
            <SortableTh
              property="publishedAt"
              title="Published"
              setSort={setSort}
              sort={sort}
            />
            <SortableTh
              property="views"
              title="Total Views"
              setSort={setSort}
              sort={sort}
            />
            <SortableTh
              property="duration"
              title="Duration"
              setSort={setSort}
              sort={sort}
            />
            <SortableTh
              property="v7"
              title="V7"
              setSort={setSort}
              sort={sort}
            />
            <SortableTh
              property="v30"
              title="V30"
              setSort={setSort}
              sort={sort}
            />
            <SortableTh
              property="vph7"
              title="VPH7"
              setSort={setSort}
              sort={sort}
            >
              <InfoTooltip
                content="Average number of views per hour in the last 7 days period among videos with selected topic"
                title="VPH7"
              />
            </SortableTh>
            <SortableTh
              property="vph30"
              title="VPH30"
              setSort={setSort}
              sort={sort}
            >
              <InfoTooltip
                content="Average number of views per hour in the last 30 days period among videos with selected topic"
                title="VPH30"
              />
            </SortableTh>
            <SortableTh
              property="v7Index"
              title="V7 Index"
              setSort={setSort}
              sort={sort}
            >
              <InfoTooltip
                content="Average V7 among videos with selected topic compared to channel's average V7 of the last 90 days"
                title="V7 Index"
              />
            </SortableTh>
            <SortableTh
              property="v30Index"
              title="V30 Index"
              setSort={setSort}
              sort={sort}
            >
              <InfoTooltip
                content="Average V30 among videos with selected topic compared to channel's average V30 of the last 90 days"
                title="V30 Index"
              />
            </SortableTh>
            <SortableTh
              property="revenue"
              title="Revenue"
              setSort={setSort}
              sort={sort}
            />
            <SortableTh
              property="rpm"
              title="RPM"
              setSort={setSort}
              sort={sort}
            >
              <InfoTooltip
                content="Average Revenue per 1000 views among videos with selected topic"
                title="Rpm"
              />
            </SortableTh>
            <SortableTh
              property="r7"
              title="R7"
              setSort={setSort}
              sort={sort}
            />
            <SortableTh
              property="r30"
              title="R30"
              setSort={setSort}
              sort={sort}
            />
            <SortableTh
              property="r7Index"
              title="R7 Index"
              setSort={setSort}
              sort={sort}
            >
              <InfoTooltip
                content="Average R7 among videos with selected topic compared to channel's average R7 of the last 90 days"
                title="R7 Index"
              />
            </SortableTh>
            <SortableTh
              property="r30Index"
              title="R30 Index"
              setSort={setSort}
              sort={sort}
            >
              <InfoTooltip
                content="Average R30 among videos with selected topic compared to channel's average R30 of the last 90 days"
                title="R30 Index"
              />
            </SortableTh>
            <LargeTh>
              Script Topics
              <InfoTooltip
                content="The main topic of the video, should be added after watching the video"
                title="Script Topics"
              />
            </LargeTh>
            <LargeTh>
              Thumb Topics
              <InfoTooltip
                content="Prominent elements in the thumbnail, that create the first impression about the video"
                title="Thumb Topics"
              />
            </LargeTh>
            <LargeTh>
              Title Topics
              <InfoTooltip
                content="Automatically assigned tags, added periodically by analyzing title"
                title="Title Topics"
              />
            </LargeTh>
            <LargeTh>
              Video Type
              <InfoTooltip
                content='Flexible field to group videos into categories like "animation", "reuse", etc'
                title="Video Type"
              />
            </LargeTh>
            <Th>
              <SrOnly>Actions</SrOnly>
            </Th>
          </tr>
        </thead>
        <tbody>
          <TableContent
            isLoading={isLoading}
            isError={isError}
            hasData={!!videos.length}
          >
            {videos.map((video) => (
              <TopTopicsInnerTableRow
                key={video.id}
                video={video}
                videosQueryKey={videosQueryKey}
              />
            ))}
          </TableContent>
        </tbody>
      </StyledTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default TopTopicsInnerTable;
